import PortalIdParser from 'PortalIdParser';
import { useCallback, useState } from 'react';
import getLang from 'I18n/utils/getLang';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { AIBadge } from 'ai-components-ui-library/components/informative/AIBadge';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import Rating from 'marketplace-ui-reviews/components/Rating';
import ReviewsFilterSortDropdowns from 'marketplace-ui-reviews/components/ReviewsFilterSortDropdowns';
import ReviewsListStats from 'marketplace-ui-reviews/components/ReviewsListStats';
import ReviewsPaginatedList from 'marketplace-ui-reviews/components/ReviewsPaginatedList';
import { WriteReviewButton } from 'marketplace-ui-reviews/components/WriteReviewButton';
import { AppReviewWizard } from 'marketplace-ui-reviews/components/wizard/apps/AppReviewWizard';
import { useReviewability } from 'marketplace-ui-reviews/hooks/useReviewability';
import { useAIReviewSummary } from 'marketplace-ui-reviews/hooks/useAIReviewSummary';
import { useReviews } from 'marketplace-ui-reviews/hooks/useReviews';
import ReviewFilters from 'marketplace-ui-reviews/records/ReviewFilters';
import LoadingWrapper from 'marketplace-ui-common/layout/components/LoadingWrapper';
import { MediaQueryView } from 'marketplace-ui-common/layout/components/MediaQueryView';
import { useDetailsPageContext } from '../../context/DetailsPageContext';
import ReviewRenderer from '../../reviews/ReviewRenderer';
import { DetailsPageCard } from './DetailsPageCard';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export function ProductReviewsCard({
  isPreview,
  partnerPortalId,
  productId,
  supportUrl,
  userId
}) {
  const [reviewWizardOpen, setReviewWizardOpen] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const {
    appContext,
    iconUrl,
    name,
    productType,
    trackInteraction
  } = useDetailsPageContext();
  const reviewType = productType;
  const [showPagination, setShowPagination] = useState(false);
  const [reviewFilters, setReviewFilters] = useState(new ReviewFilters({
    entityId: productId,
    limit: 2
  }));
  const {
    reviewSummary
  } = useAIReviewSummary({
    entityId: productId,
    language: getLang(),
    reviewType
  });

  // Gets the reviews for the listing
  const {
    reviews,
    reviewsFetchStatus,
    reviewStats,
    reviewsTotal,
    reviewsCount
  } = useReviews({
    appContext,
    reviewFilters,
    offeringId: productId,
    reviewType
  });
  const hasReviews = reviewsCount > 0;
  const handleTrackInteraction = useCallback(eventProps => {
    trackInteraction(Object.assign({
      section: 'product reviews card'
    }, eventProps));
  }, [trackInteraction]);

  // Gets the user's review if it exists
  const {
    reviews: userReviews
  } = useReviews({
    appContext,
    offeringId: productId,
    reviewFilters: new ReviewFilters({
      reviewTypes: [reviewType],
      entityId: appContext === 'public' ? undefined : productId,
      reviewerPortalId: PortalIdParser.get()
    }),
    reviewType
  });
  const userReview = userReviews[0];
  const {
    unreviewableReason,
    requestState: reviewabilityRequestStatus
  } = useReviewability({
    editPending: !!userReview && reviewSubmitted,
    entityId: productId,
    entityPortalId: partnerPortalId,
    isPublicApp: appContext === 'public' || isPreview,
    reviewPending: !userReview && reviewSubmitted,
    reviewType: 'APP'
  });
  const handleUpdateFilters = useCallback(updates => {
    let newReviewFilters = reviewFilters.updateFilters(updates);
    if (!updates.page) {
      newReviewFilters = newReviewFilters.updateFilters({
        page: 1
      });
    }
    setReviewFilters(newReviewFilters);
    Object.entries(updates).forEach(([key, value]) => {
      handleTrackInteraction({
        action: 'update review filters',
        filters: [key],
        value: value
      });
    });
  }, [handleTrackInteraction, reviewFilters]);
  let renderedReviewWizard = null;
  switch (productType) {
    case 'APP':
      renderedReviewWizard = /*#__PURE__*/_jsx(AppReviewWizard, {
        appIconUrl: iconUrl,
        appName: name,
        editedReview: userReview,
        isPublic: appContext === 'public',
        offeringId: productId,
        onClose: () => {
          setReviewWizardOpen(false);
        },
        onSuccess: () => {
          setReviewSubmitted(true);
          setReviewWizardOpen(false);
        },
        onTrackInteraction: handleTrackInteraction,
        supportLink: supportUrl,
        unreviewableReason: unreviewableReason
      });
      break;
    default:
      break;
  }
  const renderedWriteReviewButton = /*#__PURE__*/_jsx(LoadingWrapper, {
    requestStatus: reviewabilityRequestStatus,
    children: /*#__PURE__*/_jsx(WriteReviewButton, {
      disabled: isPreview,
      hasEditableReview: appContext !== 'public' && !!userReview
      // TODO: handle linking to inApp review flow for public view
      // href={appContext === 'public' ? "https://hubspot.com" : undefnied}
      ,
      onClick: () => setReviewWizardOpen(true),
      reviewPending: false,
      size: "sm",
      responsive: false,
      unreviewableReason: unreviewableReason
    })
  });
  return /*#__PURE__*/_jsxs(DetailsPageCard, {
    id: "details-page-reviews",
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "detailsPage.reviewsCard.title"
    }),
    actions: hasReviews ? renderedWriteReviewButton : null,
    children: [/*#__PURE__*/_jsxs(Grid, {
      gap: "600",
      children: [!!reviewStats.count && reviewStats.count > 3 && /*#__PURE__*/_jsxs(GridItem, {
        size: 12,
        children: [/*#__PURE__*/_jsx(MediaQueryView, {
          max: "lg",
          children: /*#__PURE__*/_jsx(ReviewsListStats, {
            hideQualityRatings: true,
            inline: false,
            onUpdateFilters: handleUpdateFilters,
            reviewStats: reviewStats,
            reviewType: reviewStats.reviewType
          })
        }), /*#__PURE__*/_jsx(MediaQueryView, {
          min: "lg",
          children: /*#__PURE__*/_jsx(ReviewsListStats, {
            hideQualityRatings: true,
            inline: true,
            onUpdateFilters: handleUpdateFilters,
            reviewStats: reviewStats,
            reviewType: reviewStats.reviewType
          })
        })]
      }), /*#__PURE__*/_jsxs(GridItem, {
        size: 12,
        children: [!!reviewSummary && /*#__PURE__*/_jsxs(View, {
          backgroundColor: "fill-surface-recessed",
          borderRadius: "200",
          padding: "800",
          children: [/*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            inlineSize: "100%",
            justifyContent: "space-between",
            marginBlockEnd: "400",
            children: [/*#__PURE__*/_jsx(Text, {
              as: "small",
              color: "text-core-subtle",
              typography: "heading-25",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "detailsPage.reviewsCard.summarizedWithAI"
              })
            }), /*#__PURE__*/_jsx(AIBadge, {})]
          }), /*#__PURE__*/_jsx(Text, {
            children: reviewSummary
          })]
        }), hasReviews && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(MediaQueryView, {
            max: "lg",
            children: /*#__PURE__*/_jsx(ReviewsFilterSortDropdowns, {
              hiddenDropdowns: ['industry', 'employees', 'service'],
              onUpdateFilters: ({
                textKey,
                value
              }) => {
                handleUpdateFilters({
                  [textKey]: value
                });
              },
              inline: false,
              reviewFilters: reviewFilters,
              reviewStats: reviewStats,
              showSearchBar: true
            })
          }), /*#__PURE__*/_jsx(MediaQueryView, {
            min: "lg",
            children: /*#__PURE__*/_jsx(ReviewsFilterSortDropdowns, {
              hiddenDropdowns: ['industry', 'employees', 'service'],
              onUpdateFilters: ({
                textKey,
                value
              }) => {
                handleUpdateFilters({
                  [textKey]: value
                });
              },
              inline: true,
              reviewFilters: reviewFilters,
              reviewStats: reviewStats,
              showSearchBar: true
            })
          })]
        }), hasReviews && /*#__PURE__*/_jsx(ReviewsPaginatedList, {
          onUpdateFilters: ({
            textKey,
            value
          }) => {
            handleUpdateFilters({
              [textKey]: value
            });
          },
          reviews: reviews,
          reviewsFetchStatus: reviewsFetchStatus,
          reviewFilters: reviewFilters,
          ReviewRenderer: ReviewRenderer,
          reviewRendererProps: {
            userId
          },
          reviewsTotal: reviewsTotal,
          reviewsPerPage: reviewFilters.limit,
          showPagination: showPagination,
          pageLimitOptions: [5, 10, 20],
          searchTerm: reviewFilters.search,
          onTrackInteraction: handleTrackInteraction
        }), !showPagination && hasReviews && reviewsCount > 2 && /*#__PURE__*/_jsx(Button, {
          onClick: () => {
            handleTrackInteraction({
              action: 'click see all reviews'
            });
            setShowPagination(true);
            handleUpdateFilters({
              limit: 5
            });
          },
          use: "link",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.reviewsCard.seeAll"
          })
        }), !hasReviews && /*#__PURE__*/_jsxs(View, {
          children: [/*#__PURE__*/_jsx(Text, {
            as: "h7",
            marginBlockEnd: "200",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.reviewsCard.empty"
            })
          }), /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            flexDirection: "row",
            gap: "200",
            marginBlockEnd: "200",
            children: [/*#__PURE__*/_jsx(Rating, {
              direction: "column",
              rating: 0,
              type: "STAR_RATING_EMPTY_STATE"
            }), /*#__PURE__*/_jsx(Text, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "detailsPage.reviewsCard.emptyReviews"
              })
            })]
          }), renderedWriteReviewButton]
        })]
      })]
    }), reviewWizardOpen && renderedReviewWizard]
  });
}